import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ReferenceList from './ReferenceList';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { trimAlternativeSources, buildNotationIndex, applyNotationIndexToContent } from '../../utils/sourcesHelper';
import ResultHeader from './ResultHeader';

const FollowUpAnswer = ({ title, question, answer, references, elasticDocuments, aiModelReference, print = false }) => {
	const { t } = useTranslation();

	const filteredReferences = references.filter((reference) => answer.includes(reference.id));
	const notationIndex = buildNotationIndex(answer, references);
	const uniqueFilteredReferences = [
		...new Map(
			filteredReferences.map((reference) => [`${reference.id}-${reference.table}-${reference.type}`, reference]),
		).values(),
	];
	const alternativeReferences = references.filter((reference) => !answer.includes(reference.id));
	const trimmedAlternativeReferences = trimAlternativeSources(alternativeReferences);

	const hasReliableReferences = filteredReferences && filteredReferences.length > 0;

	return (
		<div className='flex flex-col'>
			<div className='border-t border-tertiary'></div>
			<div className='border-l-4 border-cyan-200 pl-4 flex flex-col gap-4 pb-4'>
				<div className='flex flex-col pt-4'>
					<div className='flex flex-col gap-4 pb-4'>
						<p className='text-xs font-medium leading-[1.2] text-on-secondary uppercase'>{title}</p>
						<h1 className='text-xl font-medium text-on-secondary tracking-wide'>
							{question.charAt(0).toUpperCase() + question.slice(1)}
						</h1>
					</div>
					<ResultHeader content={applyNotationIndexToContent(answer, notationIndex)} print={print} />
					<div className='flex flex-col gap-8'>
						<div data-ai-generated='true' className='pr-20 font-base leading-[1.4] tracking-wide'>
							<Markdown
								rehypePlugins={[rehypeRaw]}
								components={{
									ul: ({ children }) => <ul className='list-disc'>{children}</ul>,
									ol: ({ children }) => <ol className='list-decimal'>{children}</ol>,
									li: ({ children }) => <li className='ml-4 mb-4'>{children}</li>,
									h5: ({ children }) => <h5 className='text-lg font-medium'>{children}</h5>,
									p: ({ children }) => <p className='mb-4 text-md font-normal'>{children}</p>,
									a: ({ children, node }) => {
										const href = node?.properties?.href || '';
										return (
											<a href={href} className='text-primary'>
												{children}
											</a>
										);
									},
									strong: ({ children }) => <span className='font-medium'>{children}</span>,
								}}
							>
								{hasReliableReferences
									? applyNotationIndexToContent(answer, notationIndex)
									: t('result_page.no_reply', { app_name: t('general.app_name') })}
							</Markdown>
						</div>
						<ReferenceList
							title={t('general.references')}
							references={uniqueFilteredReferences}
							print={print}
							notationIndex={notationIndex}
							elasticDocuments={elasticDocuments}
						/>
						{hasReliableReferences && !print && (
							<ReferenceList
								title={t('general.alternative_sources')}
								references={trimmedAlternativeReferences}
								className='mt-0'
								collapsible={true}
								defaultCollapsed={true}
								notationIndex={notationIndex}
								elasticDocuments={elasticDocuments}
							/>
						)}
					</div>
				</div>
			</div>
			<div className='border-t border-tertiary'></div>
		</div>
	);
};

FollowUpAnswer.propTypes = {
	title: PropTypes.string.isRequired,
	question: PropTypes.string.isRequired,
	answer: PropTypes.string.isRequired,
	references: PropTypes.array.isRequired,
	print: PropTypes.bool,
};

export default FollowUpAnswer;
